import Vue from 'vue';
import IColors from '../models/colors';
import Axios from 'axios';
import { IUser } from './store.user';
import { groupBy } from '../helpers/chart-helpers';
import { IHistory } from '../models/world';

interface ICityTourist {
	count: number;
	prevCount: number;
    homeRegionCode: number;
    regionName: string;
    operatorId: number;
}

interface RussiaResortCity {
    name: string;
    coordinate: string;
    cityTourists: ICityTourist[];
	cityCount: number;
	prevCityCount: number;
}

interface IRussiaResortRegion {
    code: number;
    name: string;
	russiaResortCities: RussiaResortCity[];
	regionHistory: IHistory[];
	regionCount: number;
	prevRegionCount: number;
}

interface IRussiaResortDay {
    date: Date;
	regions: IRussiaResortRegion[];
	resortHistory: IHistory[];
    grades: number[];
	dayCount: number;
	prevDayCount: number;
}

interface State {
	regionId: number;
    dayInfo: IRussiaResortDay;
	colors: IColors;
	datesWithData: Date[];
}
interface IModule {
	state: State;
	mutations: any;
	actions: any;
	getters: any;
	namespaced?: boolean;
}

const russiaResort: IModule = {
	namespaced: true,
	state: {
		dayInfo: {
			date: new Date(),
			dayCount: 0,
			prevDayCount: 0,
			grades: [0],
			resortHistory: [{
				x: new Date(),
				y: 0,
			}],
			regions: [{
				code: 0,
				name: '',
				regionCount: 0,
				prevRegionCount: 0,
				regionHistory: [{
					x: new Date(),
					y: 0,
				}],
				russiaResortCities: [{
					cityCount: 0,
					prevCityCount: 0,
					coordinate: '',
					name: '',
					cityTourists: [{
						count: 0,
						prevCount: 0,
						homeRegionCode: 0,
						operatorId: 0,
						regionName: '',
					}],
				}],
			}],
		},
		regionId: -1,
		colors: {
			nodata: '#f8f8f8',
			default: '#cfe8fc',
			hover: '#0000b3',
			border: '#808080',
		},
		datesWithData: [new Date()],
	},
	mutations: {
		setDayInfo(state: State, day: IRussiaResortDay) {
			if (!day.regions.find((v) => v.code === state.regionId)) {
				state.regionId = -1;
			}
			state.dayInfo = day;
		},
		setRegionId(state: State, regionId: number) {
			state.regionId = regionId;
		},
		setColors(state: State, colors: IColors) {
			state.colors = colors;
			state.colors.border = state.colors.border.toLowerCase();
			state.colors.default = state.colors.default.toLowerCase();
			state.colors.hover = state.colors.hover.toLowerCase();
			state.colors.nodata = state.colors.nodata.toLowerCase();
		},
		setAllowedDates(state: State, dates: Date[]) {
			state.datesWithData = dates;
		},
	},
	actions: {
		updateDayInfo(context, day?: IRussiaResortDay) {
			if (day) {
				context.commit('setDayInfo', day);
			}
		},
		updateRegionId(context, regionId: number) {
			const user = context.rootGetters['user/getUser'] as IUser;
			const isRegionUser = user.claims.some((v) => v.type === 'region');
			if (regionId === -1 && isRegionUser) {
				const userRegion = user.claims.find((v) => v.type === 'region')!.value;
				context.commit('setRegionId', +userRegion);
			} else {
				context.commit('setRegionId', regionId);
			}
		},
		updateColors(context, colors: IColors) {
			context.commit('setColors', colors);
		},
		updateAllowedDates(context, dates: Date[]) {
			context.commit('setAllowedDates', dates);
		},
	},
	getters: {
		getRegionId: (state: State) => state.regionId,
		getDayInfo: (state: State) => state.dayInfo,
		getColors: (state: State) => state.colors,
		getAllowedDates: (state: State) => state.datesWithData,
		getDataDate: (state: State) => state.dayInfo.date,
		getRegions: (state: State) => {
			return [{
				code: -1,
				name: 'РФ',
				regionCount: state.dayInfo.dayCount,
				russiaResortCities: state.dayInfo.regions.map((v) => v.russiaResortCities).flat(),
			}, ...state.dayInfo.regions];
		},
		getSelectedRegion: (state: State): IRussiaResortRegion => {
			if (state.regionId !== -1) {
				return state.dayInfo.regions.find((v) => v.code === state.regionId)!;
			} else {
				return {
					code: -1,
					name: 'РФ',
					regionCount: state.dayInfo.dayCount,
					prevRegionCount: state.dayInfo.prevDayCount,
					russiaResortCities: state.dayInfo.regions.map((v) => v.russiaResortCities).flat(),
					regionHistory: state.dayInfo.resortHistory,
				};
			}
		},
		getSummaryRegionStat: (state: State, getters) => {
			const currentRegion = getters.getSelectedRegion as IRussiaResortRegion;
			const cities = currentRegion.russiaResortCities.map((v) => v.cityTourists).flat(2) as ICityTourist[];
			const groupByHomeRegion = groupBy(cities, 'regionName');
			const resultArr: Array<{ name: string, count: number, prevCount: number }> = [];
			for (const key of Object.keys(groupByHomeRegion)) {
				const resultObj = {
					name: key,
					count: groupByHomeRegion[key].reduce((prev, curr) => prev + curr.count, 0),
					prevCount: groupByHomeRegion[key].reduce((prev, curr) => prev + curr.prevCount, 0),
				};
				resultObj.prevCount = resultObj.count - resultObj.prevCount;
				resultArr.push(resultObj);
			}
			return resultArr.sort((a, b) => {
				const aName = a.name.toLowerCase();
				const bName = b.name.toLowerCase();
				if (aName < bName) {
					return - 1;
				} else if (aName > bName) {
					return 1;
				} else {
					return 0;
				}
			});
		},
		getRegionByCode: (state: State, getters) => (code: number): IRussiaResortRegion => {
			const region = getters.getRegions.find((v) => v.code === code) as IRussiaResortRegion;
			if (region) {
				return region;
			}
			else {
				return {
					code: 0,
					name: '',
					regionCount: 0,
					prevRegionCount: 0,
					russiaResortCities: [],
					regionHistory: [],
				};
			}
		},
	},
};
export { russiaResort as default, IRussiaResortDay, IRussiaResortRegion, ICityTourist, RussiaResortCity };
